import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ServiceReply, StringID } from "userful-chronos-app-common-js/dist/models/common";
import {
    AgentConfig,
    AgentHeader,
    CannedPrompt,
} from "userful-chronos-app-common-js/dist/models/eisenhower/agent/agents";
import {
    AnomalyAcknowledge,
    AnomalyRequest,
    AnomalyRequestHeader,
} from "userful-chronos-app-common-js/dist/models/eisenhower/anomaly/anomaly";
import {
    CrudModel,
    EdgePanelNavigation,
    EdgeServer,
    EdgeServerEnable,
    EdgeServerGpuMetrics,
    EdgeServerHeader,
    EdgeServerID,
    EnableEdgeAI,
    ENavigation,
    LoadState,
    OllamaModelEdge,
    ToastInfo,
} from "userful-chronos-app-common-js/dist/models/eisenhower/edgeSetup";
import {
    InitEdgeServerHeader,
    InitEnableEdgeAI,
} from "userful-chronos-app-common-js/dist/models/eisenhower/edgeSetupInit";
import { HistoryRange } from "userful-chronos-app-common-js/dist/models/eisenhower/history";
import {
    requestAddAgents,
    requestEnableAgents,
    requestListAgents,
    requestRemoveAgents,
    requestTestAgent,
    requestUpdateAgents,
} from "./msgs/agentMsgSender";
import {
    requestAcknowlegeAllAnomalies,
    requestAnomalyAcknowledge,
    requestAnomalyUpdate,
    requestClearAllAnomalies,
    requestDeleteAnomaly,
    requestListAllAnomalies,
    requestListAnomalies,
    requestListHistory,
} from "./msgs/anomalyMsgSender";
import { requestCannedPrompts } from "./msgs/cannedMsgSender";
import { requestUpdateStatusToServer } from "./msgs/edgeMsgSender";
import {
    requestAddServer,
    requestEdgeServersGpuMetrics,
    requestEnableEdgeToServer,
    requestListServers,
    requestRemoveServer,
    requestUpdateServer,
} from "./msgs/edgeServerMsgSender";
import { requestAddModel, requestListModels, requestListModelsAll, requestRemoveModel } from "./msgs/modelMsgSender";

const initialState: {
    enableEdgeAI: EnableEdgeAI;
    eisenhowerError: ServiceReply<any>;
    navigation: ENavigation;
    edgeServers: EdgeServer[];
    edgeServerHeader: EdgeServerHeader;
    openForm: boolean;
    loadState: LoadState;
    editID: string;
    edgeModels: OllamaModelEdge[];
    toasts: ToastInfo[];
    agents: AgentConfig[];
    anomalies: AnomalyRequest[];
    history: AnomalyRequest[];
    agentID: StringID;
    currentNavigation: EdgePanelNavigation;
    cannedPrompts: CannedPrompt[];
    showAnomalyAlert: boolean;
    allGPUMetrics: EdgeServerGpuMetrics[];
    showEdgeRedirect: boolean;
    showAgentRedirect: boolean;
    showLearnMore: boolean;
} = {
    enableEdgeAI: { ...InitEnableEdgeAI() },
    eisenhowerError: null,
    navigation: "AGENTS",
    edgeServers: [],
    edgeServerHeader: InitEdgeServerHeader(),
    openForm: false,
    loadState: "RENDER",
    editID: "",
    edgeModels: [],
    toasts: [],
    currentNavigation: "Settings",
    agents: [],
    anomalies: [],
    history: [],
    agentID: { value: "" },
    cannedPrompts: [],
    showAnomalyAlert: false,
    allGPUMetrics: [],
    showEdgeRedirect: false,
    showAgentRedirect: false,
    showLearnMore: false,
};

export const eisenhowerSlice = createSlice({
    name: "eisenhowerSlice",
    initialState,
    reducers: {
        setEnableEdgeAI: (state, action: PayloadAction<EnableEdgeAI>) => {
            state.enableEdgeAI = action.payload;
        },
        clearErrors: (state) => {
            state.eisenhowerError = null;
        },
        setNavigation: (state, action: PayloadAction<ENavigation>) => {
            state.navigation = action.payload;
        },
        setListEdgeServer: (state, action: PayloadAction<EdgeServer[]>) => {
            state.edgeServers = action.payload;
        },
        setEdgeServerHeader: (state, action: PayloadAction<EdgeServerHeader>) => {
            state.edgeServerHeader = action.payload;
        },
        setOpenForm: (state, action: PayloadAction<boolean>) => {
            state.openForm = action.payload;
        },
        changeLoadState: (state, action: PayloadAction<LoadState>) => {
            state.loadState = action.payload;
        },
        addOrUpdateServer: (state, action: PayloadAction<EdgeServer>) => {
            const foundIndex = state.edgeServers.findIndex((item) => item.id.value === action.payload.id.value);
            if (foundIndex < 0) {
                state.edgeServers = [...state.edgeServers, { ...action.payload }];
            } else {
                state.edgeServers = [
                    ...state.edgeServers.slice(0, foundIndex),
                    { ...action.payload },
                    ...state.edgeServers.slice(foundIndex + 1),
                ];
            }
        },
        removeServer: (state, action: PayloadAction<EdgeServerID>) => {
            state.edgeServers = state.edgeServers.filter((item) => item.id.value !== action.payload.value);
        },
        removeModel: (state, action: PayloadAction<CrudModel>) => {
            const index = state.edgeModels.findIndex((e) => e.id.value === action.payload.id.value);
            if (index !== -1) {
                const filteredModels = state.edgeModels[index].models.filter(
                    (item) => item.id !== action.payload.model,
                );
                state.edgeModels[index] = {
                    ...state.edgeModels[index],
                    models: filteredModels,
                };
            }
        },
        addEdgeServerToServer: (state, action: PayloadAction<EdgeServerHeader>) => {
            requestAddServer(action.payload);
        },
        updateEdgeServerToServer: (state, action: PayloadAction<EdgeServerHeader>) => {
            requestUpdateServer(action.payload);
        },
        removeEdgeServerFromServer: (state, action: PayloadAction<EdgeServerID>) => {
            requestRemoveServer(action.payload);
        },
        setEditID: (state, action: PayloadAction<string>) => {
            state.editID = action.payload;
        },
        addOrUpdateOllamaModelEdge: (state, action: PayloadAction<OllamaModelEdge>) => {
            const index = state.edgeModels.findIndex((e) => e.id.value === action.payload.id.value);
            if (index !== -1) {
                state.edgeModels[index] = action.payload;
            } else {
                state.edgeModels.push(action.payload);
            }
        },
        setEdgeModels: (state, action: PayloadAction<OllamaModelEdge[]>) => {
            state.edgeModels = action.payload;
        },
        requestListModelsToServer: (state, action: PayloadAction<EdgeServerID>) => {
            const dataPaylaod = action.payload.value === "" ? { value: state.editID } : action.payload;
            requestListModels(dataPaylaod);
        },
        requestAddModelToServer: (state, action: PayloadAction<CrudModel>) => {
            requestAddModel(action.payload);
        },
        requestDeleteModelToServer: (state, action: PayloadAction<CrudModel>) => {
            requestRemoveModel(action.payload);
        },
        removeToast: (state, action: PayloadAction<string>) => {
            state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
        },
        addOrUpdateToast: (state, action: PayloadAction<ToastInfo>) => {
            const index = state.toasts.findIndex((t) => t.id === action.payload.id);

            if (index !== -1) {
                // stop if user has dismissed the toast
                const existing = state.toasts[index];
                if (existing?.userDismissed) return;

                const completed = action.payload.completed;
                const total = action.payload.total;
                const prevTotal = state.toasts[index].total;
                // Update the existing toast
                if (
                    state.toasts[index].completed < action.payload.completed &&
                    completed <= total &&
                    prevTotal === total
                )
                    state.toasts[index] = action.payload;
            } else {
                // Add a new toast
                if (action.payload.total !== 0) state.toasts.push(action.payload);
            }
        },
        setToastDismissed: (state, action: PayloadAction<string>) => {
            const toast = state.toasts.find((t) => t.id === action.payload);
            if (toast) toast.userDismissed = true;
        },

        setAgentID: (state, action: PayloadAction<StringID>) => {
            state.agentID = action.payload;
        },
        setAgents: (state, action: PayloadAction<AgentConfig[]>) => {
            state.agents = action.payload;
        },
        addOrUpdateAgents: (state, action: PayloadAction<AgentConfig>) => {
            const index = state.agents.findIndex((t) => t.id.value === action.payload.id.value);

            if (index !== -1) {
                state.agents[index] = action.payload;
            } else {
                state.agents.push(action.payload);
            }
        },
        removeAgent: (state, action: PayloadAction<StringID>) => {
            state.agents = state.agents.filter((t) => t.id.value !== action.payload.value);
        },
        requestAddAgentToServer: (state, action: PayloadAction<AgentHeader>) => {
            requestAddAgents(action.payload);
        },
        requestUpdateAgentToServer: (state, action: PayloadAction<AgentHeader>) => {
            requestUpdateAgents(action.payload);
        },
        requestDeleteAgentToServer: (state, action: PayloadAction<StringID>) => {
            requestRemoveAgents(action.payload);
        },
        requestEnableAgentToServer: (state, action: PayloadAction<{ id: StringID; enabled: boolean }>) => {
            requestEnableAgents(action.payload);
        },
        setCurrentNavigation: (state, action: PayloadAction<EdgePanelNavigation>) => {
            state.currentNavigation = action.payload;
        },
        // this to enable the edge ai in platform
        requestEnableEdgeAIToServer: (state, action: PayloadAction<EnableEdgeAI>) => {
            requestUpdateStatusToServer(action.payload);
        },
        // this is to enable individual edge server
        requestEnableEdgeServerToServer: (state, action: PayloadAction<EdgeServerEnable>) => {
            requestEnableEdgeToServer(action.payload);
        },
        requestTestAgentToServer: (state, action: PayloadAction<StringID>) => {
            requestTestAgent(action.payload);
        },
        setAnomalies: (state, action: PayloadAction<AnomalyRequest[]>) => {
            state.anomalies = action.payload;
        },
        addOrUpdateAnomaly: (state, action: PayloadAction<AnomalyRequest>) => {
            const index = state.anomalies.findIndex((t) => t.id.value === action.payload.id.value);

            if (index !== -1) {
                // Update the existing toast
                state.anomalies[index] = action.payload;
            } else {
                // Add a new toast
                state.anomalies.push(action.payload);
            }
            if (!state.showAnomalyAlert) state.showAnomalyAlert = true;
        },
        requestRemoveAnomalyToServer: (state, action: PayloadAction<StringID>) => {
            requestDeleteAnomaly(action.payload);
        },
        removeAnomaly: (state, action: PayloadAction<StringID>) => {
            state.anomalies = state.anomalies.filter((t) => t.id.value !== action.payload.value);
        },
        requestAnomaliesFromServer: (state, action: PayloadAction<HistoryRange>) => {
            requestListAnomalies(action.payload);
        },
        requestAllAnomaliesFromServer: (state) => {
            requestListAllAnomalies();
        },
        requestHistoryFromServer: (state, action: PayloadAction<HistoryRange>) => {
            requestListHistory(action.payload);
        },
        setHistory: (state, action: PayloadAction<AnomalyRequest[]>) => {
            state.history = action.payload;
        },
        requestUpdateAnomalyToServer: (state, action: PayloadAction<AnomalyRequestHeader>) => {
            requestAnomalyUpdate(action.payload);
        },
        requestRemoveHistory: (state, action: PayloadAction<StringID>) => {
            state.history = state.history.filter((t) => t.id.value !== action.payload.value);
        },
        requestClearAllAnomaliesToServer: (state) => {
            requestClearAllAnomalies();
        },
        requestAcknowlegeAllAnomaliesToServer: (state) => {
            requestAcknowlegeAllAnomalies();
        },
        requestAcknowledgeAnomalyToServer: (state, action: PayloadAction<AnomalyAcknowledge>) => {
            requestAnomalyAcknowledge(action.payload);
        },
        requestListAgentsFromServer: (state) => {
            requestListAgents();
        },
        requestListServersFromServer: (state) => {
            requestListServers();
        },
        requestListModelsAllFromServer: (state) => {
            requestListModelsAll();
        },
        requestCannedPromptsFromServer: (state) => {
            requestCannedPrompts();
        },
        requestAllGPUMetricsFromServer: (state) => {
            requestEdgeServersGpuMetrics();
        },
        setCannedPrompts: (state, action: PayloadAction<CannedPrompt[]>) => {
            state.cannedPrompts = action.payload;
        },
        setShowAnomalyAlert: (state, action: PayloadAction<boolean>) => {
            state.showAnomalyAlert = action.payload;
        },
        setAllGPUMetrics: (state, action: PayloadAction<EdgeServerGpuMetrics[]>) => {
            state.allGPUMetrics = action.payload;
        },
        setShowEdgeRedirect: (state, action: PayloadAction<boolean>) => {
            state.showEdgeRedirect = action.payload;
        },
        setShowLearnMore: (state, action: PayloadAction<boolean>) => {
            state.showLearnMore = action.payload;
        },
        setShowAgentRedirect: (state, action: PayloadAction<boolean>) => {
            state.showAgentRedirect = action.payload;
        },
    },
});

export const eisenhowerActions = eisenhowerSlice.actions;

export default eisenhowerSlice.reducer;
